/**
 * Product costs — the `product_costs` flow component and PDF block (QL-417).
 */

import type { CostOverrideMode, ProductCostOverrideInfo } from "./cost-overrides";
import type { CostTypeCategory } from "./portfolio-collections";

export const PRODUCT_COSTS_SOURCES = ["new", "existing"] as const;

/** Which holdings a portfolio-scoped product-costs read describes. "existing"
 * is honoured only for followup portfolios; anything else falls back to "new".
 * Derived from the list above so the two cannot drift. */
export type ProductCostsSource = (typeof PRODUCT_COSTS_SOURCES)[number];

export const isProductCostsSource = (value: unknown): value is ProductCostsSource =>
  (PRODUCT_COSTS_SOURCES as readonly unknown[]).includes(value);

/** The `?source=` query param: absent means "new", anything unknown is null (a 400). */
export const parseProductCostsSource = (raw: string | undefined): ProductCostsSource | null =>
  raw === undefined ? "new" : isProductCostsSource(raw) ? raw : null;

/** The coverage figure for the "Only X% … analyzed" note, formatted to one
 * decimal ("85", "85.3"), or null when coverage is unknown or rounds to 100%
 * (so the note never reads "Only 100%"). */
export const formatPartialCoverage = (coveragePct: number | null | undefined): string | null => {
  if (coveragePct === null || coveragePct === undefined) return null;
  const rounded = coveragePct.toFixed(1);
  return Number(rounded) >= 100 ? null : rounded.replace(/\.0$/, "");
};

export type ProductCostTypeInfo = {
  id: string;
  name: string;
  category: `${CostTypeCategory}`;
  /** QL-20: advisor reads only — whether the advisor may override this fee. */
  overrideMode?: CostOverrideMode;
};

export type ProductCostRow = {
  /** null = existing holding the collection could not match to a product. */
  id: string | null;
  name: string;
  isin?: string;
  /** Percent of the whole portfolio (0–100). Absent on the global view. */
  weight?: number;
  /** costTypeId → percent. A missing key means no cost data. */
  costValues: Record<string, number>;
  /** QL-20: costTypeId → the catalogue value of a cell the advisor overrode
   * (costValues already holds the override). Absent when nothing is overridden. */
  overrides?: Record<string, ProductCostOverrideInfo>;
};

export type ProductCostsData = {
  products: ProductCostRow[];
  costTypes: ProductCostTypeInfo[];
  /** costTypeId → weighted percent over the rows that have that cost, or null
   * when no row has it. null for the whole field on the global view. */
  weighted: Record<string, number | null> | null;
  /** Percent of portfolio weight with any cost data. null on the global view. */
  coveragePct: number | null;
  /** The source actually served, after the non-followup fallback. */
  source: ProductCostsSource;
};

export type AllProductCostsData = {
  global: ProductCostsData;
  byPortfolioId: Record<string, ProductCostsData>;
};
